import {
    useState,
    useEffect,
    useRef,
    lazy,
    Fragment,
    Suspense,
} from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import Container from '@mui/material/Container';
import Address from 'js/helpers/Address';
import SearchBarV2 from '../searchBar/SearchBarV2';
import RapidBreadcrumbs from '../../../components/global/RapidBreadcrumbs';
import ReportMenu from '../../reports/ReportMenu';
import SidebarMenu from '../SidebarMenu';
import PropertyCMA from '../../reports/PropertyCMA';
import CustomPrintButton from '../../property/print/CustomPrintButton';
import PropertyPrintHeader from '../PropertyPrintHeader';

import {
    routeCodes,
    getRoute,
    isHomePage,
    isBeforeHomepagePathname,
    isBeforeTnCPathname,
    routeConfigs,
} from '../../../constants/routes';
import Segment from 'js/constants/segment';
import { viewHomeEvent } from '../../../actions/segment';
import { entryPoints } from '../../../constants/entryPoint';
import Commons from '../../../helpers/Commons';
import ReportHelper from '../../../helpers/report/ReportHelper';
import { GET_USER_DETAIL_FAIL } from '../../../actions/clapi';
import Sanitizer from '../../../helpers/Sanitizer';


import LocalStorageHelper, {
    ONE_YEAR,
    PORTAL_SIDEBAR_KEY
} from '../../../helpers/LocalStorageHelper';
import { isAU, isNZ, ORG } from '../../../constants/crux';
import PropertyDetailCommonsResponseBody from '../../../model/PropertyDetailCommonsResponseBody';
import { invalidateCruxAppError } from '../../../actions/errorHandler';
import { getRecentAddressSearches } from '../../../actions/msgApi';
import { PROPERTY_COMMONS } from '../../../actions/crux';
import NotificationBell from '.././NotificationBell';

import '../../../../css/crux/components/global/menu.scss';
import RadiusSearchButton from './RadiusSearchButton';
import WatchListDropdown from '../../watchList/dropdown/WatchListDropdown';
import Featured from '../../common/Featured';
import FeaturedV2 from '../../common/FeaturedV2';
import Roles from 'js/constants/entitlementsRole';
import Flags from 'js/constants/featureFlags';
import { LOGOS } from 'js/constants/assets';
import FormsDropdown from 'js/components/property/reiForms/formsDropdown/FormsDropdown';
import { STATE_UPPERCASE } from 'js/constants/states';
import UrlParamsExtractor from 'js/helpers/search/UrlParamsExtractor';
import PlezzelDropdown from '../../property/plezzel/PlezzelDropdown';
import PropertyHubButton from '../../property/propertyHub/PropertyHubButton';

const COTALITY_LOGO = isAU ? LOGOS.RP_HEADER : LOGOS.PG_HEADER;

const UserPanel = lazy(() => import('../UserPanel'));

const { SAVED_LIST_STAR, PROPERTY_DETAIL } = Segment.EVENT_PROPERTIES;

const {
    SEARCH_RESULT,
    NAME_SEARCH_RESULT,
    ACCOUNT,
    PROPERTY,
    MARKET_INSIGHTS,
    PROPERTY_PRINT,
    SAVED_SEARCH,
    TITLE_ORDERING,
    SAVED_LIST,
    SAVED_TERRITORY,
    RADIUS_SEARCH_RESULT,
    COMPANY_SEARCH_RESULT,
    VOLUME_FOLIO_SEARCH_RESULT,
    MULTI_LOCALITY_SEARCH,
    USER_FAVORITES,
    BUILDING_SEARCH_RESULT,
    PARCEL_SEARCH_RESULT,
    TITLE_REFERENCE_SEARCH_RESULT,
    LEGAL_DESCRIPTION_SEARCH_RESULT,
    ROLL_VALUATION_SEARCH_RESULT,
} = routeCodes;

const isVictoria = (property) => {
    const state = property?.location?.state;
    return state === STATE_UPPERCASE.VIC;
};

export const Menu = ({ match, history, location }) => {
    const dispatch = useDispatch();
    const propertyCommons = useSelector(state => PropertyDetailCommonsResponseBody.parse(state.crux.get('property').get('commons')));
    const featureFlag = useSelector(state => state.launchDarkly.get('featureFlag'));
    const rentalAvm = useSelector(state => state.clapi.get('rentalAvm'));
    const avmDetails = useSelector(state => state.clapi.get('avmDetails'));
    const clapiUsrDetail = useSelector(state => state.clapi.get('usrDetail'));
    const userSummaryApiSuccess = useSelector(state => !state.errorHandler.get('componentErrors').get(GET_USER_DETAIL_FAIL));
    const propertyCommonsSuccess = useSelector(state => !state.errorHandler.get('componentErrors').get(PROPERTY_COMMONS));
    const targetProperty = useSelector(state => state.rapid.get('targetProperty'));

    const [shouldRenderMenuSearchBar, setShouldRenderMenuSearchBar] = useState(false);
    const [shouldRenderBreadcrumb, setShouldRenderBreadcrumb] = useState(false);
    const [shouldRenderActions, setShouldRenderActions] = useState(false);
    const [shouldRenderHistoricalBanner, setShouldRenderHistoricalBanner] = useState(false);
    const [route, setRoute] = useState(null);
    const [propertyId, setPropertyId] = useState(null);
    const [displayRentalEstimate, setDisplayRentalEstimate] = useState(false);
    const [searchTypeState, setSearchTypeState] = useState('address');
    const [isPropertyExist, setIsPropertyExist] = useState(true);
    const [shouldRenderPropertyPrintHeader, setShouldRenderPropertyPrintHeader] = useState(false);

    const RPDSidebarRef = useRef(null);
    const prevMatchUrl = useRef(match.url);

    // Handle getDerivedStateFromProps logic with useEffect
    useEffect(() => {
        const currentRoute = getRoute(match.path);
        const currentDisplayRentalEstimate = ReportHelper.isRentalEstimateToBeDisplayed(rentalAvm);
        const currentIsPropertyExist = propertyCommons?.isValidProperty();

        if (currentRoute && currentRoute !== route) {
            const routeConfig = routeConfigs(currentRoute, {
                displayRentalEstimate: currentDisplayRentalEstimate,
                isPropertyExist: currentIsPropertyExist,
                propertyCommonsSuccess,
            });
            setShouldRenderMenuSearchBar(routeConfig.shouldRenderMenuSearchBar);
            setShouldRenderBreadcrumb(routeConfig.shouldRenderBreadcrumb);
            setShouldRenderActions(routeConfig.shouldRenderActions);
            setShouldRenderHistoricalBanner(routeConfig.shouldRenderHistoricalBanner);
            setShouldRenderPropertyPrintHeader(routeConfig.shouldRenderPropertyPrintHeader);
            setRoute(currentRoute);
            setDisplayRentalEstimate(currentDisplayRentalEstimate);
            setIsPropertyExist(currentIsPropertyExist);
        } else if (currentRoute === PROPERTY) {
            const currentPropertyId = propertyCommons?.propertyId;
            if (currentPropertyId === propertyId) {
                setShouldRenderBreadcrumb(true);
                setDisplayRentalEstimate(currentDisplayRentalEstimate);
                setIsPropertyExist(currentIsPropertyExist);
                setShouldRenderHistoricalBanner(true);
            } else {
                setPropertyId(currentPropertyId);
                setDisplayRentalEstimate(currentDisplayRentalEstimate);
                setIsPropertyExist(currentIsPropertyExist);
            }
        } else {
            setDisplayRentalEstimate(currentDisplayRentalEstimate);
            setIsPropertyExist(currentIsPropertyExist);
        }
    }, [match.path, rentalAvm, propertyCommons, propertyCommonsSuccess, route, propertyId]);

    const setPageTitle = () => {
        if (!route) return;

        switch (route.id) {
            case PROPERTY_PRINT.id:
            case PROPERTY.id: {
                const loc = propertyCommons?.location;
                const pageTitle = Address.getSingleLine(loc);
                if (pageTitle) {
                    document.title = route.title(pageTitle);
                }
                break;
            }
            case routeCodes.SEARCH_RESULT.id: {
                const params = UrlParamsExtractor.extract(location.search);
                const suggestionsList = params?.suggestions?.split('|') || [];
                if (suggestionsList) {
                    const title = suggestionsList
                        ?.suggestion?.[suggestionsList?.length - 1]
                        ?.suggestion
                    document.title = route.title(title);
                }
                break;
            }
            default: document.title = route.title; break;
        }
    };

    const toggleSideBar = () => {
        if (!route) return;

        const isOpened = LocalStorageHelper.getItem(PORTAL_SIDEBAR_KEY, true);

        if (RPDSidebarRef.current &&
            isHomePage(route) &&
            !isOpened) {
            // delay is required to avoid race condition
            Commons.delay(1).then(() => RPDSidebarRef?.current?.setActivePane(true));
            LocalStorageHelper.setItemWithExpiry(PORTAL_SIDEBAR_KEY, true, ONE_YEAR);
        }
    };

    // Call setPageTitle and toggleSideBar when route changes
    useEffect(() => {
        if (route) {
            setPageTitle();
            toggleSideBar();
        }
    }, [route, propertyCommons, location]);

    useEffect(() => {
        if (prevMatchUrl.current !== match.url) {
            toggleSideBar();
            dispatch(getRecentAddressSearches());

            if (RPDSidebarRef.current) {
                RPDSidebarRef.current.setActivePane(false);
            }
            prevMatchUrl.current = match.url;
        }
    }, [match.url]);

    const getStickyHeaderClassName = () => {
        const stickyHeaderRoutes = [
            PROPERTY, SEARCH_RESULT, NAME_SEARCH_RESULT, ACCOUNT, PROPERTY_PRINT, MULTI_LOCALITY_SEARCH,
            MARKET_INSIGHTS, SAVED_SEARCH, SAVED_LIST, SAVED_TERRITORY, TITLE_ORDERING,
            RADIUS_SEARCH_RESULT, COMPANY_SEARCH_RESULT, VOLUME_FOLIO_SEARCH_RESULT,
            BUILDING_SEARCH_RESULT, PARCEL_SEARCH_RESULT, TITLE_REFERENCE_SEARCH_RESULT,
            LEGAL_DESCRIPTION_SEARCH_RESULT,ROLL_VALUATION_SEARCH_RESULT,
        ];

        const stickyHeaderShadowLessRoutes = [
            SEARCH_RESULT, NAME_SEARCH_RESULT, ACCOUNT, MARKET_INSIGHTS, MULTI_LOCALITY_SEARCH,
            RADIUS_SEARCH_RESULT, COMPANY_SEARCH_RESULT, VOLUME_FOLIO_SEARCH_RESULT, USER_FAVORITES,
            BUILDING_SEARCH_RESULT, PARCEL_SEARCH_RESULT, TITLE_REFERENCE_SEARCH_RESULT, LEGAL_DESCRIPTION_SEARCH_RESULT,
            ROLL_VALUATION_SEARCH_RESULT,
        ];

        let stickyHeaderClass = '';

        if (stickyHeaderRoutes.includes(route)) {
            stickyHeaderClass = 'sticky-header';
        }

        if (stickyHeaderShadowLessRoutes.includes(route)) {
            stickyHeaderClass += ' sticky-header-shadow-less';
        }

        return stickyHeaderClass;
    };

    const setSearchType = (type = '') => {
        setSearchTypeState(type.toLowerCase());
    };

    const setTooltip = (id) => (
        <ReactTooltip
            id={id}
            className="custom-tool-tip custom-tooltip-disabled tooltip-text"
            place="bottom"
            effect="solid"
            event="mouseover"
            eventOff="mouseleave"
        />
    );

    const handleLogoOnClick = () => {
        const payload = {
            event: 'Click CoreLogic Header Logo',
            route,
        };
        dispatch(viewHomeEvent(payload));
        dispatch(invalidateCruxAppError());
    };

    const { value } = targetProperty || {};
    const rapidV3Response = !!value && Sanitizer.rapidSearchToPropertyDetail(value);
    const formattedPropertyNode = Sanitizer.propertySummaryToUpdateProperty(propertyCommons);
    const isHistoricProperty = Commons.isHistoricProperty(propertyCommons);
    const displayAvmDetails = ReportHelper.isAVMDetailToBeDisplayed({ avmDetails });
    const searchTypeClass = searchTypeState.replace(' ', '-');
    const isOnTitlesNZ = isNZ && route?.path === TITLE_ORDERING.path;
    const addressState = value?.addressState;

    let address;
    let propertyIdForRender;
    if (propertyCommons) {
        address = Address.get(propertyCommons.location);
        propertyIdForRender = propertyCommons.propertyId;
    }

    return (
        <header
            id="crux-nav"
            className={`search-type-${searchTypeClass}${isHistoricProperty && shouldRenderHistoricalBanner ? ' historic' : ''} ${getStickyHeaderClassName()}`}
        >
            <div className={`crux-header ${searchTypeClass}`}>
                <div className="container-fluid heading-container">
                    <div className="nav-section">
                        {
                            !isBeforeHomepagePathname() && route &&
                            <SidebarMenu
                                ref={RPDSidebarRef}
                                    featureFlag={featureFlag}
                                    pageContext={route.pageContext}
                                    searchType={searchTypeState}
                                    route={route}
                                    match={match}
                                />
                            }
                        <div className="logo">
                            <Link to={isBeforeHomepagePathname() ? "#" : routeCodes.HOME.path} className="cl-logo" onClick={handleLogoOnClick}>
                                <img
                                    className={`cl-logo-img${isAU ? '' : '-nz'}`}
                                    alt={ORG}
                                    src={COTALITY_LOGO}
                                />
                            </Link>
                        </div>
                    </div>
                    {
                        shouldRenderMenuSearchBar && route &&
                        <SearchBarV2
                            route={route}
                            entryPoint={entryPoints[route.id].entryPoint}
                            setSearchType={setSearchType}
                            dispatch={dispatch}
                            featureFlag={featureFlag}
                            isTab={false}
                        />
                    }
                    {
                        !isBeforeTnCPathname() && route &&
                        <div className="menu-extras">
                            {
                                !isOnTitlesNZ && <NotificationBell
                                    pageContext={route.pageContext}
                                    history={history}
                                    clapiUsrDetail={clapiUsrDetail}
                                    featureFlag={featureFlag}
                                />
                            }
                            <Suspense fallback={""}>
                                <UserPanel dispatch={dispatch} route={route} history={history} />
                            </Suspense>
                        </div>
                    }
                </div>
                {
                    shouldRenderBreadcrumb && route &&
                    <Container
                        maxWidth={false}
                        className="bread-crumb-container"
                    >
                        <div className="breadcrumb-print-container">
                            <RapidBreadcrumbs
                                propertyId={formattedPropertyNode?.id}
                                history={history}
                                entryPoint={entryPoints[route.id].entryPoint}
                            />
                            {
                                shouldRenderActions &&
                                <div className="activity-bar-actions">
                                    <RadiusSearchButton
                                        rapidV3Response={rapidV3Response}
                                        dispatch={dispatch}
                                        route={route}
                                        propertyCommons={propertyCommons}
                                        targetProperty={targetProperty}
                                        clapiUsrDetail={clapiUsrDetail}
                                        isPropertyExist={isPropertyExist}
                                        match={match}
                                    />
                                    <Fragment>
                                        <CustomPrintButton
                                            addressComplete={address}
                                            propertyId={propertyIdForRender}
                                        />
                                    </Fragment>

                                    <PropertyCMA
                                        userSummaryApiSuccess={userSummaryApiSuccess}
                                        clapiUsrDetail={clapiUsrDetail}
                                        isPropertyExist={isPropertyExist}
                                        setTooltip={() => setTooltip('cma-report-button-tooltip')}
                                        isHistoricProperty={isHistoricProperty}
                                        pageContext={route.pageContext}
                                        propertyId={propertyId}
                                        dispatch={dispatch}
                                    />
                                    <Featured
                                        role={Roles.REI_FORMS}
                                        flag={Flags.FORMS_INTEGRATION}
                                        forceHide={isNZ}
                                    >
                                        <FormsDropdown
                                            addressState={addressState}
                                            historical={isHistoricProperty}
                                        />
                                    </Featured>
                                    <PlezzelDropdown
                                        propertyId={propertyIdForRender}
                                        isPropertyExist={isPropertyExist}
                                    />
                                    <PropertyHubButton
                                        propertyId={propertyIdForRender}
                                        isHistoricProperty={isHistoricProperty}
                                        isPropertyExist={isPropertyExist}
                                    />
                                    <ReportMenu
                                        tooltipId="btnMenuTooltip"
                                        userSummaryApiSuccess={userSummaryApiSuccess}
                                        reportRoles={ReportHelper.getRolesWithExcludedRoles(clapiUsrDetail)}
                                        isVictoria={isVictoria(propertyCommons)}
                                        displayRentalEstimate={displayRentalEstimate}
                                        match={match}
                                        property={formattedPropertyNode}
                                        route={route}
                                        isPropertyExist={isPropertyExist}
                                        isHistoricProperty={isHistoricProperty}
                                        displayAvmDetails={displayAvmDetails}
                                        avmDetails={avmDetails}
                                        featureFlag={featureFlag}
                                        location={propertyCommons?.location}
                                    />
                                    <FeaturedV2
                                        role={Roles.SAVED_LISTS}
                                    >
                                        <WatchListDropdown
                                            properties={[
                                                propertyIdForRender,
                                            ]}
                                            page={PROPERTY_DETAIL}
                                            entryPoint={SAVED_LIST_STAR}
                                            isHistoricProperty={isHistoricProperty}
                                            isPropertyExist={isPropertyExist}
                                            shouldCallListInfo
                                        />
                                    </FeaturedV2>
                                </div>
                            }
                        </div>
                    </Container>
                }
                {
                    isHistoricProperty && shouldRenderHistoricalBanner &&
                    <div className="historical-property-banner">
                        This property is a historic record that has since been sub-divided or amalgamated.
                    </div>
                }
                {
                    address && shouldRenderPropertyPrintHeader &&
                    <div className="container-fluid property-print-header-container">
                        <PropertyPrintHeader
                            address={address}
                            dispatch={dispatch}
                            match={match}
                        />
                    </div>
                }
            </div>
        </header >
    );
};

Menu.propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.any,
    location: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.object,
    ]),
};

export default Menu;
